import { createUseStyles } from 'react-jss';

import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Spacings } from '@picsart/design-system/foundation/spacings';

// One pass of the swell travelling across the three dots. Matches
// `FeedScrollHint`'s bounce, so the app's two "keep going" animations share a
// tempo, and leaves a beat of stillness between passes.
const DOTS_CYCLE = '1.4s';

const useStyles = createUseStyles(
  {
    // Inherits the button's own font — the pill decides how its label looks.
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
    },
    // Truncates rather than wraps: the last stage's copy is long enough to
    // outgrow the pill on the narrowest phones, and a two-line label would
    // change the pill's height mid-upload.
    message: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Centred against the text's line box rather than sat on its baseline,
    // which is where round dots belong — the period glyphs these replaced hung
    // low and uneven at this size.
    dots: {
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      gap: Spacings.s2,
      marginLeft: Spacings.s4,
    },
    dot: {
      // The box stays 4px whatever the swell does — only `transform` moves, so
      // the dots never reflow the label beside them.
      width: Spacings.s4,
      height: Spacings.s4,
      borderRadius: CornerRadius.circle,
      // Inherits the pill's own text colour, like the DS icons do, so the dots
      // can never drift from the label beside them.
      backgroundColor: 'currentColor',
      animation: `$swell ${DOTS_CYCLE} ${Easings.easeInOut} infinite`,
      '&:nth-child(2)': {
        animationDelay: '0.16s',
      },
      '&:nth-child(3)': {
        animationDelay: '0.32s',
      },
    },
    // Each dot swells and settles a beat after the one before it, so a crest
    // travels left to right and the row reads as forward motion.
    //
    // Size, at **full opacity throughout** — not the opacity fade the reference
    // implementations use (Vercel Geist, Workday Canvas, three-dots'
    // `dot-flashing`). Those all sit on neutral or white surfaces; this pill is
    // a saturated gradient, where a 4px dot at ~0.3 alpha simply disappears, so
    // an opacity-driven cycle spends most of its time with no visible ellipsis
    // at all. Uniform scale rather than the `scaleY` stretch of three-dots'
    // `dot-elastic`, too: at 4px, stretching turns the dots into ovals that read
    // as broken punctuation instead of dots.
    //
    // The hold from 60% to 100% is what separates one pass from the next —
    // without it the three just shimmer in place.
    '@keyframes swell': {
      '0%, 60%, 100%': { transform: 'scale(0.75)' },
      '30%': { transform: 'scale(1.45)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      dot: {
        animation: 'none',
      },
    },
  },
  { name: 'upload-status-label' }
);

export default useStyles;
